import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Target, MessageSquare, Bug, X, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";

/**
 * BetaFeedbackFAB — v1.0.0 founder-tester persistent feedback entry point.
 * Bottom-right pink→amber gradient pill. Only renders when user.is_founding_tester.
 * Gentle pulse every 10s. Tap opens a single bottom sheet with 3 options:
 *   - My Missions    → /beta
 *   - Send Feedback  → /feedback
 *   - Report a Bug   → /feedback?type=bug
 * Hidden on /login, /signup, and itself (/beta /feedback) so it doesn't shadow
 * the native page controls.
 */
export default function BetaFeedbackFAB() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);
  const intervalRef = useRef(null);

  // Fire a gentle pulse every 10 seconds
  useEffect(() => {
    if (!user?.is_founding_tester) return undefined;
    intervalRef.current = setInterval(() => setPulseKey((k) => k + 1), 10000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [user?.is_founding_tester]);

  if (!user?.is_founding_tester) return null;

  // Hide on pages where it would be redundant or disruptive
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (path.startsWith("/login") || path.startsWith("/signup") || path.startsWith("/beta") || path.startsWith("/feedback")) {
    return null;
  }

  const goto = (p) => { setOpen(false); navigate(p); };

  return (
    <>
      {/* Pulsing pill — bottom-right, 80px from nav bar */}
      <motion.button
        key={pulseKey}
        onClick={() => setOpen(true)}
        initial={{ scale: 1, boxShadow: "0 4px 14px rgba(236, 72, 153, 0.25)" }}
        animate={{
          scale: [1, 1.06, 1],
          boxShadow: [
            "0 4px 14px rgba(236, 72, 153, 0.25)",
            "0 8px 28px rgba(245, 158, 11, 0.55)",
            "0 4px 14px rgba(236, 72, 153, 0.25)",
          ],
        }}
        transition={{ duration: 2.0, ease: "easeInOut" }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-[88px] right-4 z-50 flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-gradient-to-r from-pink-500 to-amber-500 text-white font-bold text-xs shadow-lg ring-2 ring-white/40"
        data-testid="beta-fab"
        aria-label="Beta feedback"
      >
        <Sparkles className="w-3.5 h-3.5" />
        <span>Beta</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-black/40 z-50"
              onClick={() => setOpen(false)}
              data-testid="beta-fab-backdrop"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 260 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl px-4 pt-3 pb-6 max-w-lg mx-auto"
              data-testid="beta-fab-sheet"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto" />
                <button
                  onClick={() => setOpen(false)}
                  className="absolute top-3 right-3 p-1.5 rounded-full bg-slate-100"
                  data-testid="beta-fab-close"
                >
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              </div>

              <h2 className="text-base font-black text-slate-800 text-center mb-1">Your beta toolkit</h2>
              <p className="text-[11px] text-slate-500 text-center mb-4">
                Pick one — missions, feedback, or report a bug.
              </p>

              <div className="space-y-2">
                <button
                  onClick={() => goto("/beta")}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-pink-50 to-pink-100 hover:from-pink-100 hover:to-pink-200 border border-pink-200 transition"
                  data-testid="beta-fab-option-missions"
                >
                  <div className="w-10 h-10 rounded-xl bg-pink-500 flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-bold text-slate-800">My Missions</p>
                    <p className="text-[11px] text-slate-500">Tasks that earn points and help me understand the app</p>
                  </div>
                </button>

                <button
                  onClick={() => goto("/feedback")}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-sky-50 to-sky-100 hover:from-sky-100 hover:to-sky-200 border border-sky-200 transition"
                  data-testid="beta-fab-option-feedback"
                >
                  <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-bold text-slate-800">Send Feedback</p>
                    <p className="text-[11px] text-slate-500">Tell me what worked, what didn't, what surprised you</p>
                  </div>
                </button>

                <button
                  onClick={() => goto("/feedback?type=bug")}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 border border-red-200 transition"
                  data-testid="beta-fab-option-bug"
                >
                  <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center flex-shrink-0">
                    <Bug className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-bold text-slate-800">Report a Bug</p>
                    <p className="text-[11px] text-slate-500">Something broken? Let me know what and where.</p>
                  </div>
                </button>
              </div>

              <p className="text-[10px] text-slate-400 text-center mt-4 italic">
                Your feedback goes straight to the Founder. Every message gets read.
              </p>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
